import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import {
  Zap, ArrowLeft, Users, DollarSign, MousePointerClick, TrendingUp,
  Copy, Check, Share2, ExternalLink, Clock, Gift, ShieldCheck,
  Send, MessageCircle, Twitter, Link as LinkIcon, Star, RefreshCw,
  UserPlus, Award, ChevronRight, Sparkles
} from 'lucide-react';
import { AreaChart, Area, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, ComposedChart } from 'recharts';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const BASE_URL = 'https://ore-extraction-3.preview.emergentagent.com';

// Copy button helper
function CopyButton({ text, label }) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <button onClick={handleCopy}
      className="flex items-center gap-1 px-3 py-1.5 bg-white/5 text-slate-400 hover:text-white text-xs font-mono transition-all hover:bg-white/10"
      data-testid={`copy-${label}`}>
      {copied ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
      {copied ? t('common.copied') : t('common.copy')}
    </button>
  );
}

// Social Share Buttons
function ShareButtons({ shareData }) {
  if (!shareData) return null;

  const openShare = (platform) => {
    let url;
    switch (platform) {
      case 'twitter':
        url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareData.messages.twitter)}`;
        break;
      case 'whatsapp':
        url = `https://wa.me/?text=${encodeURIComponent(shareData.messages.whatsapp)}`;
        break;
      case 'telegram':
        url = `https://t.me/share/url?url=${encodeURIComponent(shareData.link)}&text=${encodeURIComponent(shareData.messages.telegram)}`;
        break;
      default: return;
    }
    window.open(url, '_blank', 'width=600,height=400');
  };

  return (
    <div className="flex gap-2" data-testid="share-buttons">
      <button onClick={() => openShare('twitter')}
        className="flex items-center gap-1.5 px-3 py-2 bg-[#1DA1F2]/10 text-[#1DA1F2] text-xs font-mono hover:bg-[#1DA1F2]/20 transition-all"
        data-testid="share-twitter">
        <Twitter className="w-4 h-4" /> Twitter/X
      </button>
      <button onClick={() => openShare('whatsapp')}
        className="flex items-center gap-1.5 px-3 py-2 bg-[#25D366]/10 text-[#25D366] text-xs font-mono hover:bg-[#25D366]/20 transition-all"
        data-testid="share-whatsapp">
        <MessageCircle className="w-4 h-4" /> WhatsApp
      </button>
      <button onClick={() => openShare('telegram')}
        className="flex items-center gap-1.5 px-3 py-2 bg-[#0088CC]/10 text-[#0088CC] text-xs font-mono hover:bg-[#0088CC]/20 transition-all"
        data-testid="share-telegram">
        <Send className="w-4 h-4" /> Telegram
      </button>
      <CopyButton text={shareData.link} label="referral-link" />
    </div>
  );
}

// Application Form
function ApplicationForm({ onSubmit }) {
  const { t } = useTranslation();
  const [form, setForm] = useState({ reason: '', social_links: '', audience_size: '', preferred_slug: '' });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const links = form.social_links ? form.social_links.split(',').map(s => s.trim()).filter(Boolean) : [];
      await axios.post(`${BACKEND_URL}/api/referrals/apply`, {
        reason: form.reason,
        social_links: links,
        audience_size: form.audience_size,
        preferred_slug: form.preferred_slug || null,
      }, { withCredentials: true });
      onSubmit();
    } catch (err) {
      alert(err.response?.data?.detail || 'Error al enviar solicitud');
    }
    setLoading(false);
  };

  return (
    <div className="bg-[#090B0F] border border-white/10 p-6 max-w-2xl mx-auto" data-testid="promoter-application-form">
      <div className="flex items-center gap-2 mb-6">
        <Star className="w-6 h-6 text-amber-400" />
        <h2 className="text-lg font-bold text-white">{t('promoter.applyTitle')}</h2>
      </div>
      <p className="text-sm text-slate-400 mb-6">
        Como promotor de BlackOre X, recibiras <span className="text-emerald-400 font-bold">$10 por cada usuario</span> que
        se registre con tu codigo. Tus referidos obtienen <span className="text-emerald-400 font-bold">$5 de credito + 30 dias premium</span>.
      </p>

      <div className="space-y-4">
        <div>
          <label className="text-xs text-slate-400 mb-1 block">{t('promoter.whyPromote')}</label>
          <textarea value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })}
            placeholder="Tengo una comunidad de trading con 5K seguidores..."
            rows={3}
            className="w-full bg-[#11141D] border border-white/10 text-white px-3 py-2 font-mono text-sm focus:border-emerald-500/50 outline-none resize-none"
            data-testid="apply-reason" />
        </div>
        <div>
          <label className="text-xs text-slate-400 mb-1 block">{t('promoter.socialLinks')}</label>
          <input value={form.social_links} onChange={(e) => setForm({ ...form, social_links: e.target.value })}
            placeholder="https://twitter.com/tu_cuenta, https://instagram.com/tu_cuenta"
            className="w-full bg-[#11141D] border border-white/10 text-white px-3 py-2 font-mono text-sm focus:border-emerald-500/50 outline-none"
            data-testid="apply-social-links" />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-xs text-slate-400 mb-1 block">{t('promoter.audienceSize')}</label>
            <select value={form.audience_size} onChange={(e) => setForm({ ...form, audience_size: e.target.value })}
              className="w-full bg-[#11141D] border border-white/10 text-white px-3 py-2 font-mono text-sm focus:border-emerald-500/50 outline-none"
              data-testid="apply-audience-size">
              <option value="">{t('promoter.select')}</option>
              <option value="0-1K">0 - 1,000</option>
              <option value="1K-10K">1,000 - 10,000</option>
              <option value="10K-50K">10,000 - 50,000</option>
              <option value="50K-100K">50,000 - 100,000</option>
              <option value="100K+">100,000+</option>
            </select>
          </div>
          <div>
            <label className="text-xs text-slate-400 mb-1 block">{t('promoter.customSlug')}</label>
            <div className="flex items-center">
              <span className="text-[10px] text-slate-600 font-mono mr-1">/ref/</span>
              <input value={form.preferred_slug} onChange={(e) => setForm({ ...form, preferred_slug: e.target.value })}
                placeholder="mi-nombre"
                className="flex-1 bg-[#11141D] border border-white/10 text-white px-3 py-2 font-mono text-sm focus:border-emerald-500/50 outline-none"
                data-testid="apply-slug" />
            </div>
          </div>
        </div>
        <button onClick={handleSubmit} disabled={loading || !form.reason}
          className="w-full bg-amber-500 text-black py-2.5 font-bold text-sm hover:bg-amber-400 transition-colors disabled:opacity-30"
          data-testid="submit-application-btn">
          {loading ? t('common.loading') : t('promoter.title')}
        </button>
      </div>
    </div>
  );
}

// Active Promoter Dashboard
function ActiveDashboard({ dashboard, shareData, onRefresh }) {
  const { t } = useTranslation();
  const stats = dashboard.stats || {};
  const chart = dashboard.chart_data || [];
  const referrals = dashboard.recent_referrals || [];

  return (
    <div className="space-y-6" data-testid="promoter-active-dashboard">
      {/* Referral Link */}
      <div className="bg-[#090B0F] border border-emerald-500/30 p-5">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <LinkIcon className="w-5 h-5 text-emerald-500" />
            <h3 className="text-sm uppercase tracking-[0.2em] font-semibold text-white">{t('promoter.yourLink')}</h3>
          </div>
          <span className="text-xs font-mono text-emerald-400 bg-emerald-400/10 px-2 py-0.5">{t('promoter.statusActive')}</span>
        </div>
        <div className="flex items-center gap-2 bg-[#11141D] border border-white/10 p-3 mb-4">
          <code className="flex-1 font-mono text-sm text-emerald-400 select-all" data-testid="referral-link-display">
            {dashboard.referral_link}
          </code>
          <CopyButton text={dashboard.referral_link} label="main-link" />
        </div>
        <div className="flex items-center gap-2 mb-4">
          <span className="text-xs text-slate-500">Codigo:</span>
          <code className="font-mono text-sm text-white bg-white/5 px-2 py-0.5" data-testid="referral-code-display">{dashboard.code}</code>
          <CopyButton text={dashboard.code} label="code" />
        </div>
        <ShareButtons shareData={shareData} />
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4" data-testid="promoter-stats">
        <div className="bg-[#090B0F] border border-white/10 p-4">
          <div className="flex items-center gap-1 mb-1">
            <MousePointerClick className="w-4 h-4 text-blue-400" />
            <p className="text-[10px] uppercase tracking-wider text-slate-500">{t('promoter.clicks')}</p>
          </div>
          <p className="text-2xl font-black text-white font-mono" data-testid="stat-clicks">{stats.total_clicks || 0}</p>
        </div>
        <div className="bg-[#090B0F] border border-white/10 p-4">
          <div className="flex items-center gap-1 mb-1">
            <UserPlus className="w-4 h-4 text-emerald-400" />
            <p className="text-[10px] uppercase tracking-wider text-slate-500">{t('promoter.signups')}</p>
          </div>
          <p className="text-2xl font-black text-emerald-400 font-mono" data-testid="stat-signups">{stats.total_signups || 0}</p>
        </div>
        <div className="bg-[#090B0F] border border-white/10 p-4">
          <div className="flex items-center gap-1 mb-1">
            <TrendingUp className="w-4 h-4 text-cyan-400" />
            <p className="text-[10px] uppercase tracking-wider text-slate-500">{t('promoter.conversion')}</p>
          </div>
          <p className="text-2xl font-black text-cyan-400 font-mono" data-testid="stat-conversion">{stats.conversion_rate || 0}%</p>
        </div>
        <div className="bg-[#090B0F] border border-white/10 p-4">
          <div className="flex items-center gap-1 mb-1">
            <DollarSign className="w-4 h-4 text-amber-400" />
            <p className="text-[10px] uppercase tracking-wider text-slate-500">{t('promoter.earned')}</p>
          </div>
          <p className="text-2xl font-black text-amber-400 font-mono" data-testid="stat-earnings">${stats.total_earnings || 0}</p>
        </div>
        <div className="bg-[#090B0F] border border-white/10 p-4">
          <div className="flex items-center gap-1 mb-1">
            <Gift className="w-4 h-4 text-purple-400" />
            <p className="text-[10px] uppercase tracking-wider text-slate-500">{t('promoter.perSignup')}</p>
          </div>
          <p className="text-2xl font-black text-purple-400 font-mono">${stats.credit_per_signup || 10}</p>
        </div>
      </div>

      {/* Chart */}
      <div className="bg-[#090B0F] border border-white/10 p-4">
        <div className="flex items-center gap-2 mb-4">
          <TrendingUp className="w-5 h-5 text-emerald-500" />
          <h3 className="text-sm uppercase tracking-[0.2em] font-semibold text-white">{t('promoter.activity30d')}</h3>
        </div>
        <div className="h-[200px]" data-testid="referral-chart">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={chart}>
              <defs>
                <linearGradient id="colorClicks" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#3B82F6" stopOpacity={0} />
                </linearGradient>
              </defs>
              <XAxis dataKey="date" axisLine={false} tickLine={false}
                tick={{ fill: '#64748B', fontSize: 9 }}
                tickFormatter={(v) => v.slice(5)} interval={4} />
              <YAxis axisLine={false} tickLine={false} tick={{ fill: '#64748B', fontSize: 9 }} />
              <Tooltip
                contentStyle={{ backgroundColor: '#11141D', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 0, fontSize: 11 }}
                labelStyle={{ color: '#94A3B8' }} />
              <Area type="monotone" dataKey="clicks" stroke="#3B82F6" strokeWidth={2}
                fillOpacity={1} fill="url(#colorClicks)" name="Clicks" />
              <Bar dataKey="signups" fill="#10B981" name="Registros" />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Recent Referrals */}
      <div className="bg-[#090B0F] border border-white/10 p-4">
        <div className="flex items-center gap-2 mb-4">
          <Users className="w-5 h-5 text-emerald-500" />
          <h3 className="text-sm uppercase tracking-[0.2em] font-semibold text-white">{t('promoter.recentReferrals')}</h3>
        </div>
        {referrals.length === 0 ? (
          <div className="text-center py-6">
            <Users className="w-8 h-8 text-slate-600 mx-auto mb-2" />
            <p className="text-slate-500 text-xs font-mono">{t('promoter.noReferrals')}</p>
          </div>
        ) : (
          <div className="space-y-2">
            {referrals.map((ref, idx) => (
              <div key={idx} className="flex items-center justify-between bg-[#11141D] border border-white/5 p-3">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-emerald-500/20 flex items-center justify-center">
                    <UserPlus className="w-4 h-4 text-emerald-400" />
                  </div>
                  <div>
                    <p className="text-sm text-white font-medium">{ref.referred_name || ref.referred_email}</p>
                    <p className="text-[10px] font-mono text-slate-500">
                      {new Date(ref.created_at).toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric' })}
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-sm font-mono text-amber-400">+${ref.credit_given || 10}</p>
                  <p className="text-[10px] text-slate-500">{t('promoter.earnedLabel')}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}


// Main Page
export default function PromoterPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [dashboard, setDashboard] = useState(null);
  const [shareData, setShareData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchDashboard = useCallback(async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/referrals/my-dashboard`, { withCredentials: true });
      setDashboard(data);

      if (data.is_promoter && data.status === 'active') {
        const shareRes = await axios.get(`${BACKEND_URL}/api/referrals/share-data`, { withCredentials: true });
        setShareData(shareRes.data);
      }
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchDashboard(); }, [fetchDashboard]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#030508] flex items-center justify-center">
        <RefreshCw className="w-8 h-8 text-emerald-500 animate-spin" />
      </div>
    );
  }

  const isPending = dashboard?.status === 'pending';
  const isActive = dashboard?.is_promoter && dashboard?.status === 'active';
  const isRejected = dashboard?.status === 'rejected';

  return (
    <div className="min-h-screen bg-[#030508]" data-testid="promoter-page">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-[#030508]/80 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-6xl mx-auto px-4 md:px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/dashboard" className="text-slate-400 hover:text-white transition-colors">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-white flex items-center justify-center">
                <Zap className="w-5 h-5 text-[#030508]" />
              </div>
              <span className="text-xl font-black tracking-tight text-white" style={{ fontFamily: 'Chivo' }}>
                BlackOre<span className="text-emerald-500">X</span>
              </span>
              <span className="text-xs font-mono text-amber-400 ml-2">{t('promoter.title')}</span>
            </div>
          </div>
          {isActive && (
            <button onClick={fetchDashboard}
              className="flex items-center gap-1 px-3 py-1.5 text-slate-400 hover:text-white text-xs font-mono transition-colors">
              <RefreshCw className="w-3.5 h-3.5" /> {t('common.refresh')}
            </button>
          )}
        </div>
      </header>

      <main className="max-w-6xl mx-auto p-4 md:p-6 space-y-6">
        {/* Pending status */}
        {isPending && (
          <div className="bg-amber-500/10 border border-amber-500/30 p-6 text-center" data-testid="pending-status">
            <Clock className="w-10 h-10 text-amber-400 mx-auto mb-3" />
            <h2 className="text-lg font-bold text-white mb-2">{t('promoter.pendingTitle')}</h2>
            <p className="text-sm text-slate-400">{t('promoter.pendingDesc')}</p>
            <p className="text-xs font-mono text-slate-500 mt-2">Enviada: {new Date(dashboard.applied_at).toLocaleDateString('es-ES')}</p>
          </div>
        )}

        {/* Rejected status */}
        {isRejected && (
          <div className="bg-red-500/10 border border-red-500/30 p-6 text-center" data-testid="rejected-status">
            <ShieldCheck className="w-10 h-10 text-red-400 mx-auto mb-3" />
            <h2 className="text-lg font-bold text-white mb-2">{t('promoter.rejectedTitle')}</h2>
            <p className="text-sm text-slate-400">{t('promoter.rejectedDesc')}</p>
          </div>
        )}

        {/* Active Dashboard */}
        {isActive && <ActiveDashboard dashboard={dashboard} shareData={shareData} onRefresh={fetchDashboard} />}

        {/* Application Form (for non-promoters) */}
        {!dashboard?.is_promoter && !isPending && !isRejected && (
          <>
            {/* Benefits banner */}
            <div className="bg-[#090B0F] border border-white/10 p-6" data-testid="promoter-benefits">
              <div className="flex items-center gap-2 mb-4">
                <Sparkles className="w-6 h-6 text-amber-400" />
                <h2 className="text-lg font-bold text-white">{t('promoter.programTitle')}</h2>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-[#11141D] border border-white/5 p-4">
                  <DollarSign className="w-8 h-8 text-amber-400 mb-2" />
                  <h3 className="text-sm font-bold text-white mb-1">{t('promoter.earnPerUser')}</h3>
                  <p className="text-xs text-slate-400">{t('promoter.earnPerUser')}</p>
                </div>
                <div className="bg-[#11141D] border border-white/5 p-4">
                  <Gift className="w-8 h-8 text-purple-400 mb-2" />
                  <h3 className="text-sm font-bold text-white mb-1">{t('promoter.benefitsTitle')}</h3>
                  <p className="text-xs text-slate-400">{t('promoter.benefitsDesc')}</p>
                </div>
                <div className="bg-[#11141D] border border-white/5 p-4">
                  <Share2 className="w-8 h-8 text-blue-400 mb-2" />
                  <h3 className="text-sm font-bold text-white mb-1">{t('promoter.customLinkTitle')}</h3>
                  <p className="text-xs text-slate-400">{t('promoter.customLinkDesc')}</p>
                </div>
              </div>
            </div>
            <ApplicationForm onSubmit={fetchDashboard} />
          </>
        )}
      </main>
    </div>
  );
}
